import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Shield, Award, MapPin, Phone, ChevronRight, Check, AlertTriangle, Bird, Home, Bug, Calculator, ChevronLeft, RotateCcw, Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Header, Logo } from "../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEO Component
const PageSEO = () => (
  <>
    <title>Solar Bird Proofing Ipswich & Brisbane | Pigeon Control | Pest Free</title>
    <meta name="description" content="Professional solar panel bird proofing in Ipswich & Brisbane. Pigeon nest removal, premium mesh with 10-year warranty. Book a $20 inspection today." />
    <meta name="keywords" content="solar panel bird proofing Brisbane, pigeon control Ipswich, solar panel mesh installation, bird proofing Queensland, pigeon removal Brisbane, bird netting, bird spiking, cat enclosures Brisbane" />
    <link rel="canonical" href="https://pestfreebrisbane.com.au/birds" />
    <meta property="og:title" content="Solar Panel Bird Proofing Ipswich & Brisbane | Pest Free Ipswich & Brisbane" />
    <meta property="og:description" content="Professional solar panel bird proofing with 10-year warranty. Book a $20 inspection today." />
    <meta property="og:url" content="https://pestfreebrisbane.com.au/birds" />
  </>
);

// Theme colors for birds - Blue
const theme = {
  primary: "#5BC0DE",
  primaryDark: "#4AA8C4",
  secondary: "#7ABC32",
  gradient: "from-[#5BC0DE] to-[#007AFF]"
};

// Trust Badge Component
const TrustBadge = ({ icon: Icon, text, color = "green" }) => (
  <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-sm border border-slate-100">
    <div className={`p-1.5 rounded-full ${color === "blue" ? "bg-blue-100" : "bg-green-100"}`}>
      <Icon className={`w-4 h-4 ${color === "blue" ? "text-[#5BC0DE]" : "text-[#7ABC32]"}`} />
    </div>
    <span className="text-sm font-medium text-slate-700">{text}</span>
  </div>
);

export default function BirdsPage() {
  const calculatorRef = useRef(null);
  
  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen">
      <PageSEO />
      <Header />
      
      {/* Hero Section */}
      <HeroSection onEstimateClick={scrollToCalculator} />
      
      {/* Installation Gallery - above Services */}
      <InstallationGallery />
      
      {/* Services Section */}
      <ServicesSection />
      
      {/* Before/After Gallery - swapped with Problem Section */}
      <BeforeAfterSection />
      
      {/* Inline Estimate Calculator */}
      <div ref={calculatorRef}>
        <EstimateCalculatorSection />
      </div>
      
      {/* Problem Section - swapped down */}
      <ProblemSection />
      
      {/* Warranty Section */}
      <WarrantySection />
      
      {/* Location Section */}
      <LocationSection />
      
      {/* Final CTA */}
      <FinalCTA onEstimateClick={scrollToCalculator} />
    </div>
  );
}

// Hero Section
const HeroSection = ({ onEstimateClick }) => (
  <section className="relative pt-32 pb-20 overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0">
      <img src="https://customer-assets.emergentagent.com/job_fd293a20-78a6-4d86-9d01-01576b2818e2/artifacts/wr9qdr2u_IMG_3279.jpg" alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#5BC0DE]/70" />
    </div>
    <div className="absolute inset-0 bg-slate-900/20" />
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="text-white">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
            <Bird className="w-5 h-5" />
            <span className="font-medium">Bird & Pigeon Control</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight">
            Solar Panel Bird Proofing <span className="text-yellow-300">Ipswich & Brisbane</span>
          </h1>
          
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Premium pigeon control for homes in Ipswich and Brisbane. We remove nests and install a professional-grade mesh system backed by a 10-year warranty.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={onEstimateClick}
              className="group relative overflow-hidden rounded-full px-8 py-4 font-semibold text-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
              style={{ background: 'linear-gradient(90deg, #7ABC32, #5fa828)' }}
              data-testid="hero-estimate-btn"
            >
              <span className="relative flex items-center justify-center gap-2 text-white">
                <Calculator className="w-5 h-5" />
                Get Instant Estimate
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </button>
            <a 
              href="tel:0737081422"
              className="group relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300 hover:scale-105 bg-transparent border-2 border-white/50 hover:border-white text-white backdrop-blur-sm"
            >
              <Phone className="w-5 h-5" />
              (07) 3708 1422
            </a>
          </div>
        </div>
        
        {/* Right column - removed old photo */}
      </div>
    </div>
  </section>
);

// Before/After Carousel
const BeforeAfterSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const projects = [
    {
      before: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/tuhw31zd_IMG_5759.jpeg",
      after: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/6e943s9p_IMG_5764.jpeg",
      altBefore: "Dirty tile roof under solar panels with debris",
      altAfter: "Solar panels with bird mesh protection Ipswich"
    },
    {
      before: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/h62hrxig_IMG_1139.jpeg",
      after: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/fyli5lun_IMG_1138.jpeg",
      altBefore: "Heavy bird droppings and debris on flat roof solar panels",
      altAfter: "Clean roof with bird mesh protection installed"
    },
    {
      before: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/4jsedrwx_IMG_0695.png",
      after: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/oru8lnou_IMG_0696.png",
      altBefore: "Blocked gutters with pigeon droppings under solar panels",
      altAfter: "Clean gutters after professional gutter clean service"
    },
    {
      before: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/orqq6r1b_IMG_6862.jpeg",
      after: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/aukjev1y_IMG_6879.jpeg",
      altBefore: "Bird netting damage with droppings on commercial building",
      altAfter: "Clean bird netting repair on commercial building"
    },
    {
      before: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/2tvr2ew5_IMG_7006.png",
      after: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/0dqgo655_IMG_7005.png",
      altBefore: "Bird spikes with pigeon mess Brisbane",
      altAfter: "Clean bird spike installation after treatment"
    },
    {
      before: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/65g4836q_IMG_2924.jpeg",
      after: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/9vkz1ylq_IMG_2950.jpeg",
      altBefore: "Solar panels before bird mesh installation Brisbane",
      altAfter: "Solar panels with premium mesh protection installed"
    },
    {
      before: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/ocf5bdxj_IMG_5450.jpeg",
      after: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/i4rz1qj7_IMG_5453.jpeg",
      altBefore: "Dirty solar panels with bird droppings on white tin roof",
      altAfter: "Clean solar panels after treatment Brisbane"
    }
  ];

  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 400);
  };

  const scrollPrev = () => {
    const newIndex = currentSlide === 0 ? projects.length - 1 : currentSlide - 1;
    goToSlide(newIndex);
  };

  const scrollNext = () => {
    const newIndex = currentSlide === projects.length - 1 ? 0 : currentSlide + 1;
    goToSlide(newIndex);
  };

  const currentProject = projects[currentSlide];

  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-white to-slate-50" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 md:mb-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-3">
            See Why <span className="text-[#7ABC32]">Bird Proofing</span> Matters
          </h2>
          <p className="text-base md:text-lg text-slate-600">Real results from Ipswich and Brisbane homes</p>
        </div>
        
        <div className="relative">
          <div className="flex">
            {/* Before Image */}
            <div className="relative flex-1">
              <div className="absolute top-4 left-4 z-10 bg-red-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">BEFORE</div>
              <div className="rounded-l-2xl overflow-hidden">
                <img 
                  src={currentProject.before}
                  alt={currentProject.altBefore}
                  className="w-full h-[280px] sm:h-[340px] md:h-[420px] lg:h-[480px] object-cover transition-opacity duration-300"
                  draggable="false"
                  key={`before-${currentSlide}`}
                />
              </div>
            </div>
            
            {/* Divider */}
            <div className="w-[3px] bg-white z-10 shrink-0" />
            
            {/* After Image */}
            <div className="relative flex-1">
              <div className="absolute top-4 right-4 z-10 bg-[#7ABC32] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">AFTER</div>
              <div className="rounded-r-2xl overflow-hidden">
                <img 
                  src={currentProject.after}
                  alt={currentProject.altAfter}
                  className="w-full h-[280px] sm:h-[340px] md:h-[420px] lg:h-[480px] object-cover transition-opacity duration-300"
                  draggable="false"
                  key={`after-${currentSlide}`}
                />
              </div>
            </div>
          </div>

          {/* Left Arrow */}
          <button 
            onClick={scrollPrev}
            disabled={isAnimating}
            className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex items-center justify-center bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full transition-all disabled:opacity-50"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </button>

          {/* Right Arrow */}
          <button 
            onClick={scrollNext}
            disabled={isAnimating}
            className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex items-center justify-center bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full transition-all disabled:opacity-50"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </button>

          {/* Counter */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-black/40 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">
            {currentSlide + 1} / {projects.length}
          </div>
        </div>
        
        {/* Dots */}
        <div className="flex justify-center gap-2 mt-5">
          {projects.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isAnimating}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide 
                  ? "w-8 md:w-10 h-2.5 md:h-3 bg-[#5BC0DE]" 
                  : "w-2.5 md:w-3 h-2.5 md:h-3 bg-slate-300 hover:bg-slate-400"
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => (
  <section className="py-16 md:py-20 bg-white" data-testid="birds-services">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
          Our Bird Control <span className="text-[#5BC0DE]">Services</span>
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Complete bird management solutions for homes and businesses.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { 
            title: "Solar Panel Bird Mesh", 
            desc: "Premium mesh installation to stop birds nesting under your solar panels. 10-year warranty available." 
          },
          { 
            title: "Bird Netting", 
            desc: "Professional-grade netting to protect carports, balconies, and commercial spaces from birds." 
          },
          { 
            title: "Bird Spiking", 
            desc: "Humane deterrent spikes for ledges, signs, and roosting areas." 
          },
          { 
            title: "Nest Removal & Cleanup", 
            desc: "Safe removal of bird nests and thorough cleanup of droppings and debris." 
          },
          { 
            title: "Cat/Pet Enclosures", 
            desc: "Custom-built outdoor enclosures to keep your pets safe while enjoying the outdoors." 
          },
          { 
            title: "Bird Mite Treatment", 
            desc: "Eliminate bird mites that can invade your home from nearby nests." 
          }
        ].map((service, i) => (
          <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-[#5BC0DE]/30 hover:shadow-lg transition-all">
            <div className="inline-flex p-3 bg-[#5BC0DE]/10 rounded-xl mb-4">
              <Check className="w-6 h-6 text-[#5BC0DE]" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">{service.title}</h3>
            <p className="text-slate-600">{service.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Problem Gallery Section - Showcasing real work
const ProblemSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const galleryImages = [
    {
      url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/9oxtf7ic_IMG_0695.png",
      caption: "Blocked gutters from nesting pigeons",
      position: "center"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/33gyj04l_IMG_9478.jpeg",
      caption: "Dead pigeons caused by bird mites found under panels",
      position: "center"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/o0eug5x9_IMG_9479.jpeg",
      caption: "Bird nest with cockroaches, earwigs and bird lice",
      position: "center"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/wm27euvf_IMG_1139.jpeg",
      caption: "Garden growing from gutters due to pigeons",
      position: "center"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/uby7luqb_IMG_9231.jpeg",
      caption: "Pigeon nesting under a solar panel",
      position: "center"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/m1t54t6v_IMG_8798.jpeg",
      caption: "Indian myna nesting in mailbox",
      position: "center"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/7vehae3m_20250120_125304_Original.jpeg",
      caption: "Indian myna nesting under gable",
      position: "bottom"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/0mtr8qr6_IMG_5249.jpeg",
      caption: "Pigeons taking shelter under panels",
      position: "center"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/hb1ogf4h_IMG_6862.jpeg",
      caption: "Bird damage before our intervention",
      position: "center"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_fc400e33-e984-491f-9889-ad31723ddf6e/artifacts/cfxxz4gv_IMG_2080.jpeg",
      caption: "Birds nesting under gable",
      position: "center"
    }
  ];

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);

  return (
    <section className="py-16 md:py-20 bg-slate-900" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-red-500/20 rounded-full px-4 py-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="font-medium text-red-400">Real Problems We Solve</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
            See Why <span className="text-[#5BC0DE]">Bird Proofing</span> Matters
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            These are real situations we've encountered. Don't let this happen to your property.
          </p>
        </div>
        
        {/* Main Gallery */}
        <div className="relative max-w-4xl mx-auto">
          {/* Main Image */}
          <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-slate-800">
            <img 
              src={galleryImages[currentSlide].url}
              alt={galleryImages[currentSlide].caption}
              className="w-full h-full object-cover transition-opacity duration-500"
              style={{ objectPosition: galleryImages[currentSlide].position }}
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            
            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <p className="text-white font-semibold text-lg">{galleryImages[currentSlide].caption}</p>
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Thumbnail strip - scrollable for many images */}
          <div className="flex justify-center gap-2 mt-6 overflow-x-auto pb-2 px-4">
            {galleryImages.map((img, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                  currentSlide === index 
                    ? 'border-[#5BC0DE] scale-105' 
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          {/* Slide indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {galleryImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentSlide === index ? 'bg-[#5BC0DE] w-6' : 'bg-slate-600'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Problem cards below */}
        <div className="grid md:grid-cols-4 gap-4 mt-12">
          {[
            { icon: Bug, title: "Bird Mites", desc: "Parasites that invade homes" },
            { icon: Shield, title: "Health Risks", desc: "Diseases and allergens" },
            { icon: Home, title: "Risk Losing Efficiency", desc: "Panel damage, built up debris, scratches and stains on panels" },
            { icon: AlertTriangle, title: "Fire Hazard", desc: "Dry nesting materials are perfect tinder" }
          ].map((item, i) => (
            <div key={i} className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700">
              <item.icon className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <h3 className="text-white font-semibold mb-1">{item.title}</h3>
              <p className="text-slate-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// Installation Gallery - Real job photos
const installPhotos = [
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/z5jjmahy_IMG_1795.jpeg", caption: "Close-up of our stainless steel mesh & clip system" },
  { url: "https://customer-assets.emergentagent.com/job_fd293a20-78a6-4d86-9d01-01576b2818e2/artifacts/ogla51yz_IMG_9959.jpeg", caption: "Commercial bird netting installation with scissor lift" },
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/1g87oe1v_IMG_0886.png", caption: "Fresh mesh install on brand new panels" },
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/w6vcbets_IMG_1037.jpeg", caption: "Early-stage bird proofing — protecting panels before damage", pos: "bottom" },
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/tet1ecjw_IMG_8783.jpeg", caption: "Cleaning panels with water-fed brush" },
  { url: "https://customer-assets.emergentagent.com/job_fd293a20-78a6-4d86-9d01-01576b2818e2/artifacts/hfeiqlxo_IMG_1821.jpeg", caption: "Professional mesh protection on corrugated roof" },
];

const InstallationGallery = () => {
  const [expanded, setExpanded] = useState(null);
  return (
    <>
    <section className="py-14 bg-slate-50" data-testid="installation-gallery">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Professional Installation</h2>
          <p className="text-slate-600 mt-2">See how we protect your solar investment with precision-fitted mesh</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {installPhotos.map((photo, i) => (
            <div key={i} className="group relative rounded-xl overflow-hidden shadow-md aspect-square cursor-pointer" onClick={() => setExpanded(i)}>
              <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" style={photo.pos ? {objectPosition: photo.pos} : undefined} loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
                <p className="text-white text-xs md:text-sm font-medium leading-tight">{photo.caption}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
    {expanded !== null && (
      <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setExpanded(null)}>
        <img src={installPhotos[expanded].url} alt={installPhotos[expanded].caption} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" />
        <p className="absolute bottom-6 left-0 right-0 text-center text-white text-sm font-medium">{installPhotos[expanded].caption}</p>
      </div>
    )}
    </>
  );
};

// Warranty Section
const WarrantySection = () => (
  <section id="warranty" className="py-16 md:py-20 bg-gradient-to-br from-[#7ABC32] to-[#5A8F24]" data-testid="warranty-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center text-white">
        <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
          <Award className="w-5 h-5" />
          <span className="font-medium">Industry-Leading Warranty</span>
        </div>
        
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
          10-Year Warranty on All Installations
        </h2>
        
        <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
          We stand behind our work. Our premium mesh systems are built to last, and we guarantee it with a comprehensive 10-year warranty.
        </p>
        
        <div className="flex flex-wrap justify-center gap-6">
          {["Quality Materials", "Expert Installation", "No Birds Guaranteed", "Free Re-service"].map((item, i) => (
            <div key={i} className="flex items-center gap-2 bg-white/10 rounded-full px-5 py-2">
              <Check className="w-5 h-5 text-white" />
              <span className="font-medium">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Location Section - Grouped Regional Coverage
const LocationSection = () => {
  const regions = [
    {
      name: "Ipswich Region",
      suburbs: "Ipswich CBD, Brassall, Booval, Bundamba, Redbank Plains, Goodna, Springfield, Springfield Lakes, Ripley, Deebing Heights, Yamanto, Rosewood"
    },
    {
      name: "Lockyer Valley",
      suburbs: "Gatton, Laidley, Plainland, Withcott, Helidon, Forest Hill, Grantham"
    },
    {
      name: "Brisbane South",
      suburbs: "Mt Gravatt, Sunnybank, Carindale, Wishart, Upper Mt Gravatt, Eight Mile Plains, Rochedale, Springwood"
    },
    {
      name: "Brisbane West",
      suburbs: "Indooroopilly, Kenmore, Chapel Hill, Jindalee, Mt Ommaney, Centenary, Sherwood, Graceville, Taringa"
    },
    {
      name: "Logan Corridor",
      suburbs: "Logan Central, Woodridge, Browns Plains, Marsden, Crestmead, Beenleigh, Eagleby, Waterford"
    }
  ];

  return (
    <section id="locations" className="py-16 bg-slate-50" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
            Servicing <span className="text-[#5BC0DE]">Ipswich & Brisbane</span>
          </h2>
          <p className="text-lg text-slate-600">Professional bird proofing across South East Queensland</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {regions.map((region, i) => (
            <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 hover:border-[#5BC0DE]/30 hover:shadow-md transition-all">
              <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#5BC0DE]" />
                {region.name}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">{region.suburbs}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTA = ({ onEstimateClick }) => (
  <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 to-slate-800" data-testid="final-cta">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
        Protect Your Solar Investment Today
      </h2>
      <p className="text-xl text-slate-300 mb-8">
        Don't let pigeons damage your solar panels. Get a professional quote now.
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button onClick={onEstimateClick} className="bg-[#7ABC32] hover:bg-[#6AAD28] text-white text-lg px-8 py-6 rounded-full">
          Get Instant Estimate
        </Button>
        <a href="tel:0737081422" className="inline-flex items-center justify-center gap-2 bg-[#5BC0DE] hover:bg-[#4AA8C4] text-white text-lg px-8 py-6 rounded-full font-semibold">
          <Phone className="w-5 h-5" />
          Call (07) 3708 1422
        </a>
      </div>
    </div>
  </section>
);

// Inline Estimate Calculator Section
const EstimateCalculatorSection = () => {
  const [panels, setPanels] = useState("");
  const [storeys, setStoreys] = useState("single");
  const [pigeonDuration, setPigeonDuration] = useState("<6m");
  const [addGutterCleaning, setAddGutterCleaning] = useState(false);
  const [addPanelWash, setAddPanelWash] = useState(false);
  const [addPressureWash, setAddPressureWash] = useState(false);
  const [dontKnowPanels, setDontKnowPanels] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Calculate estimate whenever inputs change
  useEffect(() => {
    const panelCount = parseInt(panels) || 0;
    if (panelCount < 1) {
      setEstimate(null);
      return;
    }

    // Tiered pricing (smooth transitions - like tax brackets)
    const tier1Rate = 42;    // panels 1-16
    const tier2Rate = 45.50; // panels 17-26
    const tier3Rate = 49;    // panels 27+
    
    // Small addition for longer nesting (slightly more cleanup)
    const nestingAdd = pigeonDuration === ">6m" ? 2 : 0;
    
    // Calculate tiered total (each tier only applies to panels in that range)
    let baseTotal;
    let baseRate; // For display purposes
    if (panelCount <= 16) {
      baseTotal = panelCount * (tier1Rate + nestingAdd);
      baseRate = tier1Rate + nestingAdd;
    } else if (panelCount <= 26) {
      baseTotal = (16 * (tier1Rate + nestingAdd)) + ((panelCount - 16) * (tier2Rate + nestingAdd));
      baseRate = tier2Rate + nestingAdd;
    } else {
      baseTotal = (16 * (tier1Rate + nestingAdd)) + (10 * (tier2Rate + nestingAdd)) + ((panelCount - 26) * (tier3Rate + nestingAdd));
      baseRate = tier3Rate + nestingAdd;
    }
    
    // Storey multipliers and additions
    let storeyMultiplier = 1;
    let storeyAddition = 0;
    
    if (storeys === "two") {
      storeyMultiplier = 1.25;
    } else if (storeys === "three") {
      storeyMultiplier = 1.4;
      storeyAddition = 400; // Boom lift fee
    }
    
    // Calculate mesh total with $550 minimum charge (inc GST)
    const calculatedMesh = Math.round((baseTotal * storeyMultiplier) + storeyAddition);
    const meshTotal = Math.max(550, calculatedMesh);
    
    // Cleaning services pricing
    const storeyCleanMult = storeys === "three" ? 1.40 : storeys === "two" ? 1.20 : 1;
    const storeyFlat = storeys === "three" ? 80 : storeys === "two" ? 50 : 0;
    const panelWashLevy = storeys === "three" ? 80 : storeys === "two" ? 50 : 0;
    const gutterCleaningPrice = Math.round((60 + panelCount * 8) * storeyCleanMult + storeyFlat);
    const panelWashPriceCalc = Math.round((25 + panelCount * 5) * storeyCleanMult + storeyFlat + panelWashLevy);
    const pressureWashPriceCalc = Math.round((50 + panelCount * 9) * storeyCleanMult + storeyFlat);

    const selectedCleaningCount = [addGutterCleaning, addPanelWash, addPressureWash].filter(Boolean).length;
    const isCleaningBundle = selectedCleaningCount >= 2;
    const rawCleaningTotal = (addGutterCleaning ? gutterCleaningPrice : 0) + (addPanelWash ? panelWashPriceCalc : 0) + (addPressureWash ? pressureWashPriceCalc : 0);
    const cleaningBundleDiscount = isCleaningBundle ? Math.round(rawCleaningTotal * 0.20) : 0;
    const cleaningTotal = rawCleaningTotal - cleaningBundleDiscount;
    
    const total = meshTotal + cleaningTotal;
    const launchDiscount = Math.round(total * 0.10);
    const discountedTotal = total - launchDiscount;

    setEstimate({ 
      panels: panelCount, 
      storeys, 
      pigeonDuration, 
      rate: baseRate, 
      storeyMultiplier,
      storeyAddition,
      meshTotal,
      gutterCleaningPrice,
      panelWashPrice: panelWashPriceCalc,
      pressureWashPrice: pressureWashPriceCalc,
      selectedCleaningCount,
      isCleaningBundle,
      cleaningBundleDiscount,
      cleaningTotal,
      total,
      launchDiscount,
      discountedTotal
    });
  }, [panels, storeys, pigeonDuration, addGutterCleaning, addPanelWash, addPressureWash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!estimate) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/solar-estimate`, {
        ...formData,
        panels: estimate?.panels || 0,
        storeys: estimate?.storeys === "two" ? "Two storey" : estimate?.storeys === "three" ? "Three+ storey" : "Single storey",
        pigeonDuration: estimate?.pigeonDuration === ">6m" ? "More than 6 months" : "Less than 6 months",
        points: 0,
        ratePerPanel: estimate?.rate,
        meshTotal: estimate?.meshTotal || 0,
        gutterCleaning: addGutterCleaning,
        gutterCleaningPrice: estimate?.gutterCleaningPrice || 0,
        panelWash: addPanelWash,
        panelWashPrice: estimate?.panelWashPrice || 0,
        pressureWash: addPressureWash,
        pressureWashPrice: estimate?.pressureWashPrice || 0,
        cleaningBundleDiscount: estimate?.cleaningBundleDiscount || 0,
        estimatedTotal: estimate?.total || 0,
        dontKnowPanels,
        storeyMultiplier: estimate?.storeyMultiplier,
        storeyAddition: estimate?.storeyAddition,
        timestamp: new Date().toISOString()
      });
      setSubmitted(true);
      toast.success("Estimate request sent successfully!");
    } catch (error) {
      toast.error("Failed to send estimate. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetCalculator = () => {
    setPanels("");
    setStoreys("single");
    setPigeonDuration("<6m");
    setAddGutterCleaning(false);
    setAddPanelWash(false);
    setAddPressureWash(false);
    setDontKnowPanels(false);
    setEstimate(null);
    setFormData({ name: "", phone: "", email: "", address: "", message: "" });
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <section id="calculator" className="py-16 bg-slate-50" data-testid="calculator-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <div className="w-16 h-16 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">Estimate Request Sent!</h3>
            <p className="text-lg text-slate-600 mb-6">We'll contact you within 2 hours with a detailed quote.</p>
            <Button onClick={resetCalculator} className="bg-[#5BC0DE] hover:bg-[#4AA8C4] text-white rounded-full px-8 py-4">
              Get Another Estimate
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="calculator" className="py-16 bg-slate-50" data-testid="calculator-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#5BC0DE]/10 rounded-full px-4 py-2 mb-4">
            <Calculator className="w-4 h-4 text-[#5BC0DE]" />
            <span className="text-sm font-medium text-[#5BC0DE]">Get Your Price in 60 Seconds</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900">
            Solar Panel Bird Proofing <span className="text-[#5BC0DE]">Calculator</span>
          </h2>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="grid lg:grid-cols-5">
            {/* Left Column - Calculator Options */}
            <div className="lg:col-span-3 p-6 md:p-8">
              <div className="space-y-6">
                {/* Panel Count */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Number of Solar Panels</Label>
                  <Input 
                    type="number" 
                    min="1" 
                    value={panels} 
                    onChange={(e) => { setPanels(e.target.value); setDontKnowPanels(false); }} 
                    placeholder="e.g., 20" 
                    className="text-lg py-6"
                    disabled={dontKnowPanels}
                  />
                  <button type="button" onClick={() => { setDontKnowPanels(!dontKnowPanels); if (!dontKnowPanels) setPanels(""); }} className={`mt-2 text-sm font-medium transition-colors ${dontKnowPanels ? 'text-[#7ABC32]' : 'text-[#5BC0DE] hover:text-[#3498aa]'}`} data-testid="dont-know-panels-btn">
                    {dontKnowPanels ? "I know my panel count" : "I don't know my panel count"}
                  </button>
                </div>
                
                {/* House Height */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">House Height</Label>
                  <div className="flex gap-2">
                    {[
                      { value: "single", label: "1 Storey" },
                      { value: "two", label: "2 Storey" },
                      { value: "three", label: "3+ Storey" }
                    ].map(opt => (
                      <button key={opt.value} type="button" onClick={() => setStoreys(opt.value)}
                        className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${
                          storeys === opt.value
                            ? "bg-[#5BC0DE] text-white border-2 border-[#5BC0DE]"
                            : "border-2 border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {storeys === "two" && <p className="text-xs text-slate-500 mt-2 italic">*2 technicians required — harness system & higher ladder access</p>}
                  {storeys === "three" && <p className="text-xs text-slate-500 mt-2 italic">*Boom lift hire, harness system & 2 technicians required</p>}
                </div>
                
                {/* Pigeon Duration */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">How long have pigeons been nesting?</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "<6m", label: "Less than 6 months" },
                      { value: ">6m", label: "More than 6 months" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setPigeonDuration(option.value);
                          if (option.value === "<6m") { setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); }
                        }}
                        className={`p-4 rounded-xl border-2 text-center font-medium transition-all ${
                          pigeonDuration === option.value
                            ? "border-[#5BC0DE] bg-[#5BC0DE]/10 text-[#5BC0DE]"
                            : "border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Cleaning Services - 3 separate options */}
                {pigeonDuration === ">6m" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">Cleaning Services</span>
                      {estimate?.isCleaningBundle && <span className="text-xs bg-[#7ABC32] text-white px-2 py-0.5 rounded-full">Bundle — save 20%!</span>}
                    </div>
                    {estimate?.selectedCleaningCount > 0 && estimate?.selectedCleaningCount < 2 && (
                      <p className="text-xs text-amber-600 font-medium">Select 2+ for a 20% bundle discount</p>
                    )}
                    <div className="space-y-2">
                      {[
                        { key: "gutter", active: addGutterCleaning, toggle: setAddGutterCleaning, label: "Gutter Clean", desc: "Full gutter flush & debris removal", price: estimate?.gutterCleaningPrice, details: ["Remove all debris from gutters", "Flush & clear downpipes", "Dispose of waste material"] },
                        { key: "panel", active: addPanelWash, toggle: setAddPanelWash, label: "Panel Wash", desc: "Surface wash panel tops", price: estimate?.panelWashPrice, details: ["Water-fed broom clean on panel tops", "Remove bird droppings & residue", "Restore panel efficiency"] },
                        { key: "pressure", active: addPressureWash, toggle: setAddPressureWash, label: "Pressure Wash", desc: "Pressure wash & sanitise", price: estimate?.pressureWashPrice, details: ["High-pressure wash around panels", "Sanitise & deodorise affected areas", "Remove staining from roof surface"] },
                      ].map(opt => (
                        <label key={opt.key} className={`block p-3 rounded-xl cursor-pointer border-2 transition-colors ${opt.active ? 'bg-[#7ABC32]/10 border-[#7ABC32]' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`} data-testid={`cleaning-${opt.key}`}>
                          <div className="flex items-center gap-3">
                            <Checkbox checked={opt.active} onCheckedChange={opt.toggle} />
                            <span className="font-semibold text-slate-900 text-sm whitespace-nowrap">{opt.label}</span>
                            <span className="text-xs text-slate-500 hidden sm:inline">— {opt.desc}</span>
                            {opt.price && <span className="text-[#7ABC32] font-bold text-sm ml-auto">${opt.price}</span>}
                          </div>
                          {opt.active && (
                            <ul className="mt-2 ml-8 space-y-0.5">
                              {opt.details.map((d, i) => <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />{d}</li>)}
                            </ul>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Column - Price & Form */}
            <div className="lg:col-span-2 bg-slate-50 p-6 md:p-8 border-t lg:border-t-0 lg:border-l border-slate-200">
              {/* Unified Pricing Box with Logo */}
              <div className={`rounded-2xl overflow-hidden mb-6 ${(estimate || dontKnowPanels) ? 'bg-[#5BC0DE]' : 'bg-slate-300'}`}>
                {/* Header with Logo and Promo */}
                <div className={`px-5 py-3 flex items-center justify-between ${(estimate || dontKnowPanels) ? 'bg-[#3498aa]' : 'bg-slate-400'}`}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png"
                    alt="Pest Free"
                    className="h-10"
                  />
                  {estimate && <span className="text-amber-400 text-sm font-bold whitespace-nowrap">🎉 10% OFF LAUNCH SPECIAL</span>}
                </div>
                
                {/* Main Price */}
                <div className="p-5 text-white">
                  <p className="text-sm text-white/80 mb-1">Estimated Price</p>
                  {dontKnowPanels ? (
                    <div>
                      <p className="text-2xl font-bold mb-1">No worries!</p>
                      <p className="text-sm text-white/80">Fill in your details and we'll call you with a free quote</p>
                    </div>
                  ) : estimate ? (
                    <>
                      <div className="flex items-baseline gap-3">
                        <span className="text-xl text-white/50 line-through">${estimate.total.toLocaleString()}</span>
                        <span className="text-4xl font-bold">${estimate.discountedTotal.toLocaleString()}</span>
                      </div>
                      <p className="text-amber-400 font-semibold text-sm mt-1">You save ${estimate.launchDiscount.toLocaleString()} (10% off)</p>
                      <p className="text-xs text-yellow-300 font-semibold mt-1 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Offer ends March 31</p>
                      
                      {/* Price Breakdown */}
                      <div className="mt-4 pt-4 border-t border-white/20 space-y-1 text-sm">
                        <div className="flex justify-between text-white/80">
                          <span>Solar proofing mesh</span>
                          <span><span className="line-through text-white/40 mr-1">${estimate.meshTotal.toLocaleString()}</span>${Math.round(estimate.meshTotal * 0.9).toLocaleString()}</span>
                        </div>
                        {estimate.storeyMultiplier > 1 && (
                          <p className="text-xs text-white/60 pl-2">incl. height loading</p>
                        )}
                        {estimate.storeyAddition > 0 && (
                          <p className="text-xs text-white/60 pl-2">incl. ${estimate.storeyAddition} boom lift</p>
                        )}
                        {estimate.cleaningTotal > 0 && (
                          <div className="flex justify-between text-white/80">
                            <span>+ Cleaning services</span>
                            <span>${estimate.cleaningTotal}</span>
                          </div>
                        )}
                        {estimate.isCleaningBundle && (
                          <p className="text-xs text-[#7ABC32] pl-2">incl. 20% bundle discount</p>
                        )}
                      </div>
                      <div className="text-white/50 text-[10px] mt-1">Prices include GST</div>
                    </>
                  ) : (
                    <div className="text-4xl font-bold">$—</div>
                  )}
                </div>
              </div>
              
              {/* What's Included */}
              <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
                <p className="font-semibold text-slate-900 mb-2">What's Included:</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> Removal of all nests & debris</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> Disposal of all nests</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> Blow debris with petrol blower</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> Professional-grade mesh</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> 10-year warranty on mesh</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> Before & after photos/videos</li>
                  {(addGutterCleaning || addPanelWash || addPressureWash) && (
                    <>
                      {addGutterCleaning && <li className="flex items-center gap-2 text-[#7ABC32] font-medium"><Check className="w-4 h-4" /> Full gutter flush & debris removal</li>}
                      {addPanelWash && <li className="flex items-center gap-2 text-[#7ABC32] font-medium"><Check className="w-4 h-4" /> Solar panel surface wash</li>}
                      {addPressureWash && <li className="flex items-center gap-2 text-[#7ABC32] font-medium"><Check className="w-4 h-4" /> Pressure wash & sanitise</li>}
                    </>
                  )}
                </ul>
              </div>
              
              {/* Contact Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1 block">Name *</Label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Your name"
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1 block">Phone *</Label>
                    <Input
                      required
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="0400 000 000"
                      className="bg-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1 block">Email *</Label>
                  <Input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1 block">Address *</Label>
                  <Textarea
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="e.g., 123 Example Street, Brisbane QLD 4000"
                    rows={2}
                    className="bg-white"
                  />
                </div>
                
                <p className="text-xs text-slate-500 italic">
                  Final pricing subject to inspection and property conditions.
                </p>
                
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={(!estimate && !dontKnowPanels) || submitting}
                    className="flex-1 bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 rounded-xl font-semibold"
                  >
                    {submitting ? "Sending..." : dontKnowPanels ? "Request a Callback" : "Get My Quote Now"}
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                  <Button
                    type="button"
                    onClick={resetCalculator}
                    variant="outline"
                    className="px-4 py-6 rounded-xl"
                  >
                    <RotateCcw className="w-5 h-5" />
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        {/* Quick Callback Option */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 mb-3">Prefer to talk? We'll call you back within 30 minutes.</p>
          <a 
            href="tel:0737081422"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-full font-medium transition-colors"
          >
            <Phone className="w-5 h-5" />
            Call (07) 3708 1422
          </a>
        </div>
      </div>
    </section>
  );
};
